import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Flame, Trophy } from "lucide-react";

export default function StudyStreak() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const tzOffset = new Date().getTimezoneOffset(); // JS: minutes WEST of UTC
    api
      .get("/stats/streak", { params: { tz_offset: tzOffset } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;

  const current = data.current_streak || 0;
  const best = data.longest_streak || 0;
  const showFire = current >= 3;

  return (
    <section
      data-testid="study-streak"
      className="mt-10 border-2 border-black bg-white shadow-[4px_4px_0_0_#000] px-5 py-4 sm:px-6 sm:py-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Flame
              className={`w-6 h-6 ${showFire ? "text-[#FF3333]" : "text-zinc-300"}`}
              fill={showFire ? "#FF3333" : "none"}
            />
            <div>
              <p data-testid="streak-current" className="font-heading font-black text-2xl leading-none tracking-tight">
                {current}<span className="text-base font-mono font-normal ml-1 text-zinc-500">day{current === 1 ? "" : "s"}</span>
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Current streak</p>
            </div>
          </div>

          <div className="hidden sm:block h-10 w-px bg-black/10" aria-hidden />

          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#3B82F6]" />
            <div>
              <p data-testid="streak-best" className="font-heading font-black text-2xl leading-none tracking-tight">
                {best}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Best ever</p>
            </div>
          </div>
        </div>

        <div className="flex items-end gap-1.5" data-testid="streak-week">
          {(data.week || []).map((d) => (
            <div key={d.date} className="flex flex-col items-center gap-1 min-w-[28px]">
              <div
                title={`${d.date} · ${d.count} note${d.count === 1 ? "" : "s"}`}
                className={
                  "w-7 h-7 border-2 flex items-center justify-center " +
                  (d.in_future
                    ? "border-black/10 bg-zinc-50"
                    : d.count > 0
                    ? d.is_today
                      ? "border-black bg-[#3B82F6] text-white"
                      : "border-black bg-[#BFDBFE]"
                    : d.is_today
                    ? "border-black bg-white"
                    : "border-black/20 bg-white")
                }
              >
                {d.count > 0 && (
                  <span className="font-mono text-[10px] font-bold">
                    {d.count > 9 ? "9+" : d.count}
                  </span>
                )}
              </div>
              <span
                className={
                  "font-mono text-[9px] uppercase tracking-widest " +
                  (d.is_today ? "text-black font-bold" : "text-zinc-500")
                }
              >
                {d.weekday[0]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {current === 0 && (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
          Generate a note today to start a streak.
        </p>
      )}
      {current > 0 && current < 3 && (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
          Keep going — 3-day streak unlocks the fire.
        </p>
      )}
    </section>
  );
}
